import * as configcat from '@configcat/sdk/browser'

import { DEFAULT_POLL_INTERVAL_SECONDS } from './feature-flags.consts'
import type { TFeatureFlagsConfig, TFlagsSnapshot, TFlagsSubscriber } from './feature-flags.types'

/**
 * Singleton feature flags client for ConfigCat integration.
 *
 * This package handles common infrastructure (init, polling, pub-sub reactivity, local overrides),
 * while each app remains responsible for:
 * - Defining flag name types
 * - Providing the SDK token and user object
 * - Connecting the subscriber to its state layer (MobX, React state, etc.)
 *
 * @example
 * ```ts
 * import { featureFlagsClient } from '@winnin/feature-flags'
 *
 * featureFlagsClient.init({
 *   token: 'configcat-sdk-key',
 *   getUser: () => ({ identifier: userId, email }),
 *   onEvaluate: (flag, value) => datadog.addFeatureFlagEvaluation(flag, value),
 *   localOverridesStorageKey: 'flags',
 * })
 *
 * // Pub-sub reactivity — framework-agnostic
 * const unsubscribe = featureFlagsClient.subscribe(flags => {
 *   // MobX: action(() => (this.flags = flags))
 *   // React: setState(flags)
 * })
 *
 * featureFlagsClient.isEnabled('isMyFeatureEnabled') // → boolean
 * ```
 */
export class FeatureFlagsClient {
  private config: TFeatureFlagsConfig | null = null
  private client: configcat.IConfigCatClient | null = null
  private snapshot: TFlagsSnapshot = {}
  private remoteSnapshot: TFlagsSnapshot = {}
  private subscribers: Set<TFlagsSubscriber> = new Set()
  private logger: configcat.IConfigCatLogger | null = null
  private isLoggerEnabled = false
  private storageListenerAdded = false

  /**
   * Initializes the ConfigCat client with AutoPoll.
   * Resolves after the first flag fetch (success or error — never rejects).
   */
  async init(config: TFeatureFlagsConfig): Promise<void> {
    this.config = config
    this.logger = configcat.createConsoleLogger(configcat.LogLevel.Off)
    this.setupClient()
    this.setupStorageListener()
    await this.fetchFlags()
  }

  private setupStorageListener(): void {
    if (!this.config?.localOverridesStorageKey || this.storageListenerAdded) return
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
    this.storageListenerAdded = true
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.config?.localOverridesStorageKey) void this.fetchFlags()
    })
  }

  private setupClient(): void {
    if (this.client) {
      this.client.dispose()
    }

    const { token, pollIntervalSeconds = DEFAULT_POLL_INTERVAL_SECONDS } = this.config!

    const client = configcat.getClient(token, configcat.PollingMode.AutoPoll, {
      pollIntervalSeconds,
      logger: this.logger!,
    })

    client.addListener('configChanged', () => void this.fetchFlags())
    this.client = client
  }

  /**
   * Forces an immediate flag fetch using the current result of `getUser()`.
   * Call this after updating user data (e.g., post-login).
   */
  async refreshFlags(): Promise<void> {
    await this.fetchFlags()
  }

  private async fetchFlags(): Promise<void> {
    if (!this.client || !this.config) return

    try {
      const { identifier = '', email, custom } = this.config.getUser()

      // Filter out undefined values — configcat's IUser.custom doesn't accept undefined
      const filteredCustom = custom
        ? (Object.fromEntries(Object.entries(custom).filter(([, v]) => v !== undefined)) as Record<string, string>)
        : undefined

      const userObject: configcat.IUser = { identifier, email, custom: filteredCustom }
      const values = await this.client.getAllValuesAsync(userObject)

      const fetched = values.reduce<TFlagsSnapshot>((acc: TFlagsSnapshot, item: configcat.SettingKeyValue) => {
        acc[item.settingKey] = item.settingValue
        return acc
      }, {})

      const localOverrides = this.config.localOverridesStorageKey
        ? this.readLocalOverrides(this.config.localOverridesStorageKey)
        : {}

      // Save remote values before merging overrides
      this.remoteSnapshot = { ...fetched }

      // localStorage overrides take precedence over ConfigCat values
      this.snapshot = { ...fetched, ...localOverrides }
      this.notifySubscribers()
    } catch (e) {
      console.error('@FeatureFlagsClient >>> Error fetching flags', e)
    }
  }

  /**
   * Returns whether a boolean flag is enabled.
   * Calls `onEvaluate` if configured (e.g., for Datadog RUM tracking).
   */
  isEnabled(flag: string): boolean {
    const value = this.snapshot[flag]
    this.config?.onEvaluate?.(flag, value)
    return !!value
  }

  /**
   * Returns the value of a text/number setting.
   * Calls `onEvaluate` if configured.
   */
  getValue<T>(flag: string): T | undefined {
    const value = this.snapshot[flag]
    this.config?.onEvaluate?.(flag, value)
    return value as T | undefined
  }

  /**
   * Returns a synchronous copy of the current flags snapshot.
   * Useful for initializing framework state synchronously (e.g., `useState` initial value).
   */
  getSnapshot(): TFlagsSnapshot {
    return { ...this.snapshot }
  }

  /**
   * Returns the remote (ConfigCat) flag values without local overrides applied.
   * Use this to compare against the current value and determine if a flag has been overridden.
   */
  getRemoteSnapshot(): TFlagsSnapshot {
    return { ...this.remoteSnapshot }
  }

  /**
   * Registers a subscriber that is called whenever flags are updated.
   * Returns an `unsubscribe` function.
   *
   * @example
   * ```ts
   * // MobX
   * const unsub = featureFlagsClient.subscribe(action(flags => { this.flags = flags }))
   *
   * // React
   * useEffect(() => featureFlagsClient.subscribe(setFlags), [])
   * ```
   */
  subscribe(callback: TFlagsSubscriber): () => void {
    this.subscribers.add(callback)
    return () => this.subscribers.delete(callback)
  }

  /** Returns whether the ConfigCat logger is currently enabled (Info level). */
  isLogEnabled(): boolean {
    return this.isLoggerEnabled
  }

  /**
   * Toggles the ConfigCat logger between Off and Info levels.
   * Useful for debugging flag evaluation in development.
   */
  toggleLogEvents(): void {
    this.isLoggerEnabled = !this.isLoggerEnabled
    this.logger = configcat.createConsoleLogger(this.isLoggerEnabled ? configcat.LogLevel.Info : configcat.LogLevel.Off)
    this.setupClient()
  }

  private notifySubscribers(): void {
    const snapshot = this.getSnapshot()
    this.subscribers.forEach(cb => cb(snapshot))
  }

  private readLocalOverrides(storageKey: string): TFlagsSnapshot {
    try {
      const raw = localStorage.getItem(storageKey)
      return raw ? (JSON.parse(raw) as TFlagsSnapshot) : {}
    } catch {
      return {}
    }
  }
}
